/**
 * api.ts — Central API and WebSocket URL resolution
 *
 * Supports two deployment modes:
 *  1. Same-origin (Replit dev / Replit deploy):
 *     VITE_API_URL is empty → all fetch calls use relative paths like "/api/..."
 *     VITE_WS_URL is empty  → WS connects to window.location.host/ws
 *
 *  2. Split-origin (Railway backend + Cloudflare Pages frontend):
 *     VITE_API_URL = "https://your-app.up.railway.app"
 *     VITE_WS_URL  = "wss://your-app.up.railway.app"
 *     Set these in Cloudflare Pages → Settings → Environment variables.
 */

const API_ORIGIN = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
const WS_ORIGIN  = (import.meta.env.VITE_WS_URL  as string | undefined) ?? '';

/** Production default: same-origin CF Worker (tables + fleet proxy). */
export function apiUrl(path: string): string {
  if (!API_ORIGIN) return path;
  return `${API_ORIGIN.replace(/\/$/, '')}${path}`;
}

/** Fetch with timeout + optional X-Request-Id for Railway/edge log correlation. */
export async function fetchApi(
  path: string,
  init: RequestInit = {},
  timeoutMs = 15_000,
): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const headers = new Headers(init.headers);
  if (!headers.has("X-Request-Id")) {
    headers.set(
      "X-Request-Id",
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `pg-${Date.now()}`,
    );
  }
  try {
    return await fetch(apiUrl(path), {
      ...init,
      headers,
      signal: ctrl.signal,
      credentials: init.credentials ?? "include",
    });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Build the WebSocket URL for the game server.
 * Same-origin on poker.grudge-studio.com routes /ws?table=table-N to PokerTable DO.
 */
export function wsUrl(token?: string, tableId?: string): string {
  let base: string;

  if (WS_ORIGIN) {
    base = WS_ORIGIN.replace(/\/$/, '');
  } else {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    base = `${proto}//${window.location.host}`;
  }

  const params = new URLSearchParams();
  if (tableId) params.set('table', tableId);
  if (token) params.set('token', token);
  const q = params.toString();
  return q ? `${base}/ws?${q}` : `${base}/ws`;
}
